/**
 * EmailImportModal — multi-step flow for importing clothing from email orders.
 *
 * States:
 *   1. CONNECT       — Connect Gmail (client-side OAuth)
 *   2. SEARCH_CONFIG — Configure search parameters (date range, mode, keywords)
 *   3. SCANNING      — Pipeline running with phase progress
 *   4. RESULTS       — Parsed items with select/deselect + import
 *
 * Email text is sent to Groq AI for parsing. Only structured item data
 * is saved to the backend when the user explicitly imports.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Dimmer, Loader, Icon, Button } from 'semantic-ui-react';
import { connectGmail, isGmailConnected, disconnectGmail } from '../../services/gmailAuth';
import { runEmailPipeline, PIPELINE_PHASES } from '../../services/emailPipeline';
import { getBrainInfo } from '../../services/webBrain';
import {
  useUploadClothingMutation,
  useCreateClothingFromUrlMutation
} from '../../redux/apis/endpoints/closetApi';
import OrderItemCard from './OrderItemCard';
import './EmailImportModal.css';

const MODAL_STATE = {
  CONNECT: 'connect',
  SEARCH_CONFIG: 'search_config',
  SCANNING: 'scanning',
  MODEL_LOADING: 'model_loading',
  RESULTS: 'results',
  IMPORTING: 'importing',
  PAYWALL: 'paywall',
};

/**
 * Compute a date-range object from a preset option.
 * @param {'7d'|'30d'|'3m'|'6m'|'1y'|'custom'} option
 * @param {string} [customFrom]
 * @param {string} [customTo]
 * @returns {{ after?: string, before?: string }}
 */
function getDateRange(option, customFrom, customTo) {
  const now = new Date();
  switch (option) {
    case '7d': return { after: new Date(now - 7 * 86400000).toISOString().split('T')[0] };
    case '30d': return { after: new Date(now - 30 * 86400000).toISOString().split('T')[0] };
    case '3m': return { after: new Date(now - 90 * 86400000).toISOString().split('T')[0] };
    case '6m': return { after: new Date(now - 180 * 86400000).toISOString().split('T')[0] };
    case '1y': return { after: new Date(now - 365 * 86400000).toISOString().split('T')[0] };
    case 'all': return {};
    case 'custom': return {
      after: customFrom || undefined,
      before: customTo || undefined
    };
    default: return { after: new Date(now - 30 * 86400000).toISOString().split('T')[0] };
  }
}

const EmailImportModal = ({ open, onClose, onImportSuccess }) => {
  const [state, setState] = useState(MODAL_STATE.CONNECT);
  const [gmailConnected, setGmailConnected] = useState(isGmailConnected());
  const [phase, setPhase] = useState(null);
  const [phaseDetail, setPhaseDetail] = useState('');
  const [logs, setLogs] = useState([]);
  const [brainProgress, setBrainProgress] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [error, setError] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });

  // Paywall state (for batch resume after top-up)
  const [paywallInfo, setPaywallInfo] = useState(null);
  const [parseProgress, setParseProgress] = useState({ processed: 0, total: 0 });

  const logsEndRef = useRef(null);
  useEffect(() => {
    if (state === MODAL_STATE.SCANNING) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, state]);

  // Search config state
  const [searchMode, setSearchMode] = useState('scan');
  const [dateRangeOption, setDateRangeOption] = useState('all');
  const [scanDepth, setScanDepth] = useState(100);
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [searchKeywords, setSearchKeywords] = useState('');

  const [uploadClothing] = useUploadClothingMutation();
  const [createClothingFromUrl] = useCreateClothingFromUrlMutation();

  // Reset state when modal opens
  const handleOpen = useCallback(() => {
    const connected = isGmailConnected();
    setGmailConnected(connected);
    setState(MODAL_STATE.CONNECT);
    setPhase(null);
    setPhaseDetail('');
    setLogs([]);
    setBrainProgress(null);
    setItems([]);
    setSelectedItems(new Set());
    setError(null);
    setImportProgress({ done: 0, total: 0 });
    setSearchMode('scan');
    setDateRangeOption('all');
    setScanDepth(100);
    setCustomDateFrom('');
    setCustomDateTo('');
    setSearchKeywords('');
    setSearchKeywords('');
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      handleOpen();
    }
  }, [open, handleOpen]);

  // Gmail connect
  const handleConnect = async () => {
    try {
      setError(null);
      setConnecting(true);
      await connectGmail();
      setGmailConnected(true);
    } catch (err) {
      setError(err.message || 'Failed to connect Gmail');
    } finally {
      setConnecting(false);
    }
  };

  // Gmail disconnect
  const handleDisconnect = () => {
    disconnectGmail();
    setGmailConnected(false);
    setItems([]);
    setSelectedItems(new Set());
    setState(MODAL_STATE.CONNECT);
  };

  // Transition to search config
  const handleGoToSearchConfig = () => {
    setError(null);
    setState(MODAL_STATE.SEARCH_CONFIG);
  };

  // Run the pipeline with configured options
  const handleScan = async () => {
    try {
      setError(null);
      setState(MODAL_STATE.SCANNING);
      setItems([]);
      setSelectedItems(new Set());

      const dateRange = getDateRange(dateRangeOption, customDateFrom, customDateTo);

      const results = await runEmailPipeline({
        dateRange,
        scanDepth,
        keywords: searchMode === 'keyword' ? searchKeywords : undefined,
        onPhase: (phaseInfo, detail) => {
          setPhase(phaseInfo);
          setPhaseDetail(detail.message || '');

          if (detail.message) {
            setLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), text: detail.message, phase: phaseInfo.key }]);
          }

          // If entering parse phase, model might need to download
          if (phaseInfo.key === 'parse') {
            setState(MODAL_STATE.SCANNING);
          }
        },
        onBrainProgress: (progress) => {
          if (progress.type === 'download' && progress.progress < 100) {
            setState(MODAL_STATE.MODEL_LOADING);
            setBrainProgress(progress);
          } else if (progress.type === 'init') {
            setBrainProgress(progress);
          } else {
            setState(MODAL_STATE.SCANNING);
            setBrainProgress(null);
          }
        }
      });

      if (results && results.status === 'NO_NEW_EMAILS') {
        setError(`All ${results.scannedCount} order emails were already scanned and imported. No new orders found.`);
        setItems([]);
        setSelectedItems(new Set());
        setState(MODAL_STATE.RESULTS);
        return;
      }

      if (results && results.status === 'NO_FASHION_EMAILS') {
        setError(`Found ${results.scannedCount} new order emails, but none were fashion-related.`);
        setItems([]);
        setSelectedItems(new Set());
        setState(MODAL_STATE.RESULTS);
        return;
      }

      const foundItems = results.items || results; // Handle both obj and array

      // Check if the batch was interrupted by a paywall
      if (results.paywallHit) {
        setItems(foundItems);
        setSelectedItems(new Set(foundItems.map((_, i) => i)));
        setPaywallInfo(results.paywallInfo);
        setParseProgress({ processed: results.processed, total: results.total });
        setState(MODAL_STATE.PAYWALL);
        return;
      }

      setItems(foundItems);
      // Select all by default
      setSelectedItems(new Set(foundItems.map((_, i) => i)));
      setState(MODAL_STATE.RESULTS);
    } catch (err) {
      // Catch 402 thrown directly (not from batch)
      if (err.reason === 'insufficient_credits') {
        setPaywallInfo({
          needed: err.needed,
          balance: err.balance,
          options: err.options,
        });
        setState(MODAL_STATE.PAYWALL);
        return;
      }
      setError(err.message || 'Scan failed');
      setState(gmailConnected ? MODAL_STATE.CONNECT : MODAL_STATE.CONNECT);
    }
  };

  // Toggle item selection
  const handleToggleItem = (item) => {
    const idx = items.indexOf(item);
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Select/deselect all
  const handleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((_, i) => i)));
    }
  };

  // Import selected items to closet
  const handleImport = async () => {
    const toImport = items.filter((_, i) => selectedItems.has(i));
    if (toImport.length === 0) return;

    setState(MODAL_STATE.IMPORTING);
    setImportProgress({ done: 0, total: toImport.length });
    let successCount = 0;

    for (let i = 0; i < toImport.length; i++) {
      const item = toImport[i];
      try {
        const itemName = `${item.itemName}${item.brand ? ` - ${item.brand}` : ''}`;

        if (item.imageUrl) {
          // Use createFromUrl — backend downloads the image
          await createClothingFromUrl({
            imageUrl: item.imageUrl,
            clothesName: itemName
          }).unwrap();
        } else {
          // No image — create with just the name
          await uploadClothing({
            file: null,
            clothesName: itemName,
            size: item.size || undefined
          }).unwrap();
        }
        successCount++;
      } catch (err) {
        console.warn(`Failed to import item: ${item.itemName}`, err);
      }
      setImportProgress({ done: i + 1, total: toImport.length });
    }

    if (successCount > 0 && onImportSuccess) {
      onImportSuccess(successCount);
    }
    setState(MODAL_STATE.CONNECT); // Reset state before closing
    if (onClose) onClose();
  };

  const brainInfo = getBrainInfo();

  if (!open) return null;

  const isBlocking = state === MODAL_STATE.SCANNING || state === MODAL_STATE.IMPORTING;

  const modalContent = (
    <div 
      className="email-import-modal-overlay" 
      onClick={isBlocking ? undefined : onClose}
    >
      <div 
        className="email-import-modal-container" 
        onClick={e => e.stopPropagation()}
      >
        {!isBlocking && onClose && (
          <div className="email-import-close-icon" onClick={onClose}>
            <Icon name="close" size="large" />
          </div>
        )}
        {/* ── CONNECT STATE ── */}
        {state === MODAL_STATE.CONNECT && (
          <div className="email-import-content">
            <div className="email-import-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#00b5ad" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 4L12 13L2 4" />
              </svg>
            </div>

            <h3 className="email-import-title">Import from email</h3>
            <p className="email-import-description">
              We scan your order confirmations and use AI to extract clothing items.
            </p>

            {error && (
              <div className="email-import-error">
                <Icon name="warning circle" />
                {error}
              </div>
            )}

            {gmailConnected ? (
              <div className="email-import-connected">
                <div className="email-import-connected-badge">
                  <Icon name="check circle" color="teal" />
                  Gmail connected
                  <button className="disconnect-link" onClick={handleDisconnect}>Disconnect</button>
                </div>
                <button
                  className="connect-gmail-btn"
                  onClick={handleGoToSearchConfig}
                >
                  <Icon name="search" />
                  Scan for Orders
                </button>
              </div>
            ) : (
              <button className="connect-gmail-btn" onClick={handleConnect} disabled={connecting}>
                {connecting ? (
                  <>
                    <Loader active inline size="tiny" inverted style={{ marginRight: 8 }} />
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="M22 4L12 13L2 4" />
                    </svg>
                    Connect Gmail
                  </>
                )}
              </button>
            )}

            {/* <p className="email-import-privacy">
              <Icon name="lock" size="small" />
              We only read order confirmations to find your clothing purchases.
            </p> */}
          </div>
        )}

        {/* ── SEARCH CONFIG STATE ── */}
        {state === MODAL_STATE.SEARCH_CONFIG && (
          <div className="email-import-content" style={{ alignItems: 'stretch', textAlign: 'left' }}>
            <h3 className="email-import-title" style={{ textAlign: 'center' }}>Configure scan</h3>

            {error && (
              <div className="email-import-error">
                <Icon name="warning circle" />
                {error}
              </div>
            )}

            {/* Search mode toggle */}
            <div className="search-config-section">
              <div className="search-config-label">Search Mode</div>
              <div className="search-mode-options">
                <div
                  className={`search-mode-option ${searchMode === 'scan' ? 'active' : ''}`}
                  onClick={() => setSearchMode('scan')}
                >
                  <Icon name="search" /> Scan all orders
                </div>
                <div
                  className={`search-mode-option ${searchMode === 'keyword' ? 'active' : ''}`}
                  onClick={() => setSearchMode('keyword')}
                >
                  <Icon name="keyboard" /> Search by keyword
                </div>
              </div>
            </div>

            {/* Keyword input (keyword mode only) */}
            {searchMode === 'keyword' && (
              <div className="search-config-section">
                <div className="search-config-label">Keywords</div>
                <input
                  className="search-config-input"
                  type="text"
                  placeholder="e.g. Nike, Zara dress, order confirmation…"
                  value={searchKeywords}
                  onChange={(e) => setSearchKeywords(e.target.value)}
                />
              </div>
            )}

            {/* Date range */}
            <div className="search-config-section">
              <div className="search-config-label">Date Range</div>
              <select
                className="search-config-select"
                value={dateRangeOption}
                onChange={(e) => setDateRangeOption(e.target.value)}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="3m">Last 3 months</option>
                <option value="6m">Last 6 months</option>
                <option value="1y">Last year</option>
                <option value="all">All time</option>
                <option value="custom">Custom range</option>
              </select>
              {dateRangeOption === 'custom' && (
                <div className="custom-date-range">
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Scan Depth Limit */}
            <div className="search-config-section">
              <div className="search-config-label">Max Emails to Scan</div>
              <select
                className="search-config-select"
                value={scanDepth}
                onChange={(e) => setScanDepth(Number(e.target.value))}
              >
                <option value={50}>50 emails (Fast)</option>
                <option value={100}>100 emails</option>
                <option value={250}>250 emails</option>
                <option value={500}>500 emails (Deep)</option>
              </select>
            </div>

            {/* Brain indicator */}
            <div className="brain-indicator">
              <span className="brain-dot" />
              AI: {brainInfo?.displayName || brainInfo?.modelId || 'Groq'} {brainInfo?.isLocal ? '(local)' : '(cloud)'}
            </div>

            {/* Actions */}
            <Button color="teal" fluid onClick={handleScan}>
              <Icon name="play" />
              Start Scan
            </Button>
            <button
              className="scan-again-link"
              onClick={() => setState(MODAL_STATE.CONNECT)}
              style={{ alignSelf: 'center', marginTop: 4 }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* ── SCANNING STATE ── */}
        {state === MODAL_STATE.SCANNING && (
          <div className="email-import-content email-import-scanning">
            <div className="scanning-spinner">
              <Dimmer active inverted>
                <Loader size="medium">{phaseDetail || 'Scanning...'}</Loader>
              </Dimmer>
            </div>

            <div className="scanning-phases">
              {Object.values(PIPELINE_PHASES).map((p) => {
                const isPast = phase && Object.keys(PIPELINE_PHASES).indexOf(phase.key) > Object.keys(PIPELINE_PHASES).indexOf(p.key);
                const isCurrent = phase?.key === p.key;
                if (p.key === 'done') return null;

                return (
                  <div key={p.key} className={`scanning-phase ${isCurrent ? 'active' : ''} ${isPast ? 'done' : ''}`}>
                    <Icon name={isPast ? 'check circle' : p.icon} className="icon" />
                    <span>{p.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Real-time logging terminal */}
            <div className="real-time-logs" style={{ marginTop: '16px', background: '#fcfcfc', border: '1px solid #f0f0f0', color: '#666', padding: '12px', borderRadius: '10px', width: '100%', maxWidth: '340px', maxHeight: '120px', overflowY: 'auto', textAlign: 'left', fontSize: '11px', fontFamily: 'monospace' }}>
              {logs.map((log, i) => (
                <div key={i} style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#bbb', marginRight: '6px' }}>[{log.time}]</span>
                  <span style={{ color: log.phase === 'error' ? '#db2828' : '#444' }}>{log.text}</span>
                </div>
              ))}
              {logs.length === 0 && <div style={{ color: '#bbb' }}>Initializing scanner...</div>}
              <div ref={logsEndRef} />
            </div>
          </div>
        )}

        {/* ── MODEL LOADING STATE ── */}
        {state === MODAL_STATE.MODEL_LOADING && (
          <div className="email-import-content email-import-model-loading">
            <div className="model-loading-icon">
              <Icon name="microchip" size="big" color="teal" />
            </div>
            <h3 className="email-import-title">Setting up AI</h3>
            <p className="email-import-description">
              Connecting to Groq AI...
            </p>
            <div className="model-progress-bar">
              <div
                className="model-progress-fill"
                style={{ width: `${brainProgress?.progress || 0}%` }}
              />
            </div>
            <p className="model-progress-text">
              {brainProgress?.text || 'Preparing...'}
            </p>
          </div>
        )}

        {/* ── RESULTS STATE ── */}
        {state === MODAL_STATE.RESULTS && (
          <div className="email-import-content email-import-results">
            <div className="results-header">
              <h3 className="email-import-title">
                {items.length > 0
                  ? `Found ${items.length} item${items.length !== 1 ? 's' : ''}`
                  : 'No items found'
                }
              </h3>
              {items.length > 0 && (
                <button className="select-all-link" onClick={handleSelectAll}>
                  {selectedItems.size === items.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>

            {items.length > 0 ? (
              <>
                <div className="results-list">
                  {items.map((item, i) => (
                    <OrderItemCard
                      key={`${item.sourceEmailId}-${i}`}
                      item={item}
                      selected={selectedItems.has(i)}
                      onToggle={() => handleToggleItem(item)}
                    />
                  ))}
                </div>

                <div className="results-actions">
                  <Button
                    color="teal"
                    fluid
                    onClick={handleImport}
                    disabled={selectedItems.size === 0}
                  >
                    <Icon name="plus" />
                    Import {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} to Closet
                  </Button>
                  <button className="scan-again-link" onClick={handleGoToSearchConfig}>
                    Scan again
                  </button>
                </div>
              </>
            ) : (
              <div className="results-empty">
                {error ? (
                  <div style={{ padding: '20px', background: '#f5f5f5', borderRadius: '8px', marginBottom: '16px', color: '#444' }}>
                    <Icon name="info circle" size="large" style={{ marginBottom: '12px', color: '#999' }} />
                    <p style={{ margin: 0, fontSize: '15px' }}>{error}</p>
                  </div>
                ) : (
                  <p>No fashion order confirmations found in the selected date range.</p>
                )}
                <button className="scan-again-link" onClick={handleGoToSearchConfig}>
                  Try again
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── PAYWALL STATE ── */}
        {state === MODAL_STATE.PAYWALL && (
          <div className="email-import-content" style={{ textAlign: 'center', padding: '2rem' }}>
            <Icon name="lightning" size="huge" color="yellow" style={{ marginBottom: '1rem' }} />
            <h3>AI credits needed</h3>
            {items.length > 0 ? (
              <p style={{ fontSize: '1.1rem', color: '#555' }}>
                Imported <strong>{parseProgress.processed}</strong> of <strong>{parseProgress.total}</strong> emails.
                <br />Found <strong>{items.length}</strong> item{items.length !== 1 ? 's' : ''} so far.
              </p>
            ) : (
              <p style={{ color: '#555' }}>
                Groq free tier is rate-limited and the paid model requires AI credits.
              </p>
            )}
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Credits: <strong>{paywallInfo?.balance || 0}</strong> remaining · Need <strong>{paywallInfo?.needed || 0}</strong> to continue
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                primary
                size="large"
                onClick={() => {
                  // Open Stripe checkout for PAYG top-up
                  const topUpUrl = process.env.REACT_APP_STRIPE_TOPUP_URL || '/credits';
                  window.open(topUpUrl, '_blank');
                }}
              >
                <Icon name="plus" /> Add Credits
              </Button>
              <Button
                color="teal"
                size="large"
                onClick={() => {
                  const upgradeUrl = process.env.REACT_APP_STRIPE_UPGRADE_URL || '/subscribe';
                  window.open(upgradeUrl, '_blank');
                }}
              >
                <Icon name="star" /> Subscribe
              </Button>
            </div>

            {items.length > 0 && (
              <div style={{ marginTop: '1.5rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                <Button
                  basic
                  onClick={() => {
                    // Show partial results so user can import what was parsed
                    setSelectedItems(new Set(items.map((_, i) => i)));
                    setState(MODAL_STATE.RESULTS);
                  }}
                >
                  Import {items.length} item{items.length !== 1 ? 's' : ''} found so far
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ── IMPORTING STATE ── */}
        {state === MODAL_STATE.IMPORTING && (
          <div className="email-import-content email-import-importing">
            <Dimmer active inverted>
              <Loader size="medium">
                Adding to closet ({importProgress.done}/{importProgress.total})...
              </Loader>
            </Dimmer>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default EmailImportModal;
